// The Usage demo's fixtures: the portfolio (42 vendors, 61 deals), the seven
// usage projects with their action items, and the Microsoft EA detail that
// the only drillable path reads. Totals are derived from these rows (see
// derived.ts), so a number shown on two screens is the same number.
//
// Fixed demo date: every "in N days" is computed from it.

import { LogoDevOptions, logoDevUrlForWebsite } from "../../../../common/LogoDev";
import type {
    ConsumptionDealDetail,
    ActionItem,
    SeatDealDetail,
    UsageDeal,
    UsageProject,
    UsageVendor,
} from "./types";

export const DEMO_TODAY = "2026-10-30";

export const VENDORS: UsageVendor[] = [
    { id: "microsoft", name: "Microsoft", category: "Software", website: "microsoft.com" },
    { id: "salesforce", name: "Salesforce", category: "Software", website: "salesforce.com" },
    { id: "adobe", name: "Adobe", category: "Software", website: "adobe.com" },
    { id: "zoom", name: "Zoom", category: "Software", website: "zoom.us" },
    { id: "snowflake", name: "Snowflake", category: "Cloud and data", website: "snowflake.com" },
    { id: "aws", name: "Amazon Web Services", category: "Cloud and data", website: "aws.amazon.com" },
    { id: "databricks", name: "Databricks", category: "Cloud and data", website: "databricks.com" },
    { id: "azure-openai", name: "Azure OpenAI Service", category: "AI", website: "azure.microsoft.com" },
    { id: "splunk", name: "Splunk", category: "Observability and security", website: "splunk.com" },
    { id: "dynatrace", name: "Dynatrace", category: "Observability and security", website: "dynatrace.com" },
    { id: "servicenow", name: "ServiceNow", category: "Software", website: "servicenow.com" },
    { id: "openai", name: "OpenAI", category: "AI", website: "openai.com" },
    { id: "bloomberg", name: "Bloomberg", category: "Market data", website: "bloomberg.com" },
    { id: "oracle", name: "Oracle", category: "Software", website: "oracle.com" },
    { id: "okta", name: "Okta", category: "Software", website: "okta.com" },
    { id: "atlassian", name: "Atlassian", category: "Software", website: "atlassian.com" },
    { id: "datadog", name: "Datadog", category: "Observability and security", website: "datadoghq.com" },
];

// Logos come from logo.dev, as the real vendor pages' do. Okta's mark is
// dark on transparent: the default JPEG comes back blank, so it takes the
// PNG drawn for a dark background.
const LOGO_OPTIONS: Record<string, LogoDevOptions> = {
    okta: { format: "png", theme: "dark" },
};
for (const vendor of VENDORS) vendor.hero = logoDevUrlForWebsite(vendor.website, LOGO_OPTIONS[vendor.id]);

// The named deals keep the spec's numbers exactly; the rest fill the
// categories so the tallies come out at $184.2M of spend, $132.0M of it
// with usage connected, $11.6M unused, $3.4M of overage risk and 14
// renewals in the next 180 days worth $50.6M.
export const DEALS: UsageDeal[] = [
    { id: "microsoft-ea", vendorId: "microsoft", name: "Microsoft EA (E5 and add-ons)", type: "seat", annualCost: 8500000, usageStatus: "connected", utilizationPct: 72, valueAtStake: 2900000, unusedCapacity: 2800000, term: { renews: "2027-03-31", noticeBy: "2026-12-31" }, owner: "RS" },
    { id: "microsoft-premier", vendorId: "microsoft", name: "Microsoft premier support", type: "seat", annualCost: 600000, usageStatus: "not_connected", term: { renews: "2027-03-31", noticeBy: "2026-12-31" }, owner: "RS" },
    { id: "salesforce-sales-cloud", vendorId: "salesforce", name: "Sales Cloud and Service Cloud", type: "seat", annualCost: 4200000, usageStatus: "connected", utilizationPct: 76, valueAtStake: 1200000, unusedCapacity: 1600000, term: { renews: "2027-02-12", noticeBy: "2026-11-14" }, owner: "JM" },
    { id: "salesforce-marketing", vendorId: "salesforce", name: "Marketing Cloud", type: "seat", annualCost: 1300000, usageStatus: "not_connected", term: { renews: "2027-08-31", noticeBy: "2027-06-01" }, owner: "JM" },
    { id: "adobe-cc", vendorId: "adobe", name: "Creative Cloud for enterprise", type: "seat", annualCost: 3400000, usageStatus: "connected", utilizationPct: 63, valueAtStake: 800000, unusedCapacity: 1100000, term: { renews: "2026-12-02", noticeBy: "2026-11-02" }, owner: "AM" },
    { id: "adobe-experience", vendorId: "adobe", name: "Experience Cloud", type: "seat", annualCost: 2200000, usageStatus: "partial", term: { renews: "2027-09-30", noticeBy: "2027-07-31" }, owner: "AM" },
    { id: "zoom-enterprise", vendorId: "zoom", name: "Zoom Enterprise", type: "seat", annualCost: 1100000, usageStatus: "connected", utilizationPct: 94, valueAtStake: 200000, unusedCapacity: 600000, term: { renews: "2027-02-28", noticeBy: "2027-01-29" }, owner: "RS" },
    { id: "snowflake-capacity", vendorId: "snowflake", name: "Snowflake capacity", type: "consumption", annualCost: 6000000, usageStatus: "connected", pacePct: 118, valueAtStake: 1100000, term: { renews: "2027-04-14", noticeBy: "2027-02-14" }, owner: "RS" },
    { id: "aws-edp", vendorId: "aws", name: "AWS EDP", type: "consumption", annualCost: 14000000, usageStatus: "connected", pacePct: 106, valueAtStake: 900000, term: { renews: "2027-06-30", noticeBy: "2027-03-31" }, owner: "PK" },
    { id: "aws-marketplace", vendorId: "aws", name: "AWS Marketplace private offers", type: "consumption", annualCost: 1600000, usageStatus: "not_connected", term: { renews: "2027-06-30", noticeBy: "2027-03-31" }, owner: "PK" },
    { id: "databricks-commit", vendorId: "databricks", name: "Databricks commitment", type: "consumption", annualCost: 3200000, usageStatus: "connected", pacePct: 125, valueAtStake: 800000, term: { renews: "2027-06-15", noticeBy: "2027-03-15" }, owner: "PK" },
    { id: "azure-openai", vendorId: "azure-openai", name: "Azure OpenAI provisioned throughput", type: "consumption", annualCost: 1500000, usageStatus: "connected", pacePct: 140, valueAtStake: 600000, term: { renews: "2027-07-31", noticeBy: "2027-05-31" }, owner: "PK" },
    { id: "splunk-ingest", vendorId: "splunk", name: "Splunk ingest (2.4 TB a day)", type: "consumption", annualCost: 4600000, usageStatus: "connected", pacePct: 71, valueAtStake: 1300000, unusedCapacity: 1300000, term: { renews: "2027-06-30", noticeBy: "2027-03-31" }, owner: "LG" },
    { id: "dynatrace-hosts", vendorId: "dynatrace", name: "Dynatrace (1,800 hosts)", type: "consumption", annualCost: 2300000, usageStatus: "connected", pacePct: 81, valueAtStake: 500000, unusedCapacity: 500000, term: { renews: "2027-09-30", noticeBy: "2027-06-30" }, owner: "LG" },
    { id: "servicenow-itsm", vendorId: "servicenow", name: "ITSM Pro", type: "seat", annualCost: 8100000, usageStatus: "connected", utilizationPct: 79, unusedCapacity: 900000, term: { renews: "2027-10-31", noticeBy: "2027-07-31" }, owner: "JM" },
    { id: "servicenow-hrsd", vendorId: "servicenow", name: "HR Service Delivery", type: "seat", annualCost: 1900000, usageStatus: "not_connected", term: { renews: "2027-10-31", noticeBy: "2027-07-31" }, owner: "JM" },
    { id: "chatgpt-enterprise", vendorId: "openai", name: "ChatGPT Enterprise", type: "seat", annualCost: 2100000, usageStatus: "connected", utilizationPct: 68, unusedCapacity: 600000, term: { renews: "2027-05-31", noticeBy: "2027-04-30" }, owner: "PK" },
    { id: "bloomberg-terminal", vendorId: "bloomberg", name: "Bloomberg Terminal", type: "seat", annualCost: 9200000, usageStatus: "connected", utilizationPct: 91, unusedCapacity: 600000, term: { renews: "2027-12-31", noticeBy: "2027-09-30" }, owner: "TH" },
    { id: "okta-workforce", vendorId: "okta", name: "Workforce Identity", type: "seat", annualCost: 1800000, usageStatus: "connected", utilizationPct: 90, unusedCapacity: 200000, term: { renews: "2026-12-12", noticeBy: "2026-11-12" }, owner: "LG" },
    { id: "atlassian-cloud", vendorId: "atlassian", name: "Atlassian Cloud Enterprise", type: "seat", annualCost: 1600000, usageStatus: "connected", utilizationPct: 84, unusedCapacity: 200000, term: { renews: "2027-02-20", noticeBy: "2026-12-21" }, owner: "AM" },
    { id: "datadog-infra", vendorId: "datadog", name: "Infrastructure and APM", type: "consumption", annualCost: 3100000, usageStatus: "connected", pacePct: 96, term: { renews: "2027-04-05", noticeBy: "2027-02-04" }, owner: "LG" },
    { id: "oracle-fusion", vendorId: "oracle", name: "Fusion Cloud ERP", type: "seat", annualCost: 12800000, usageStatus: "not_connected", term: { renews: "2028-01-31", noticeBy: "2027-10-31" }, owner: "PK" },
];

// Details the generated rows do not carry: the usage reading in words, the
// coverage line and the term start of the deals the demo talks about.
const DEAL_EXTRAS: Record<string, Partial<UsageDeal>> = {
    "microsoft-ea": {
        name: "Microsoft EA (E5 and add-ons)",
        term: { start: "2024-04-01", renews: "2027-03-31", noticeBy: "2026-12-31" },
        usageDetail: "8,900 of 12,400 active in 90 days",
        coverage: {
            source: "Microsoft 365 usage export",
            asOf: "2026-10-25",
            matchedPct: 94,
            note: "Entra sign-ins daily to 29 Oct; Workday headcount 20 Oct; Coupa AP 24 Oct",
        },
    },
    "salesforce-sales-cloud": {
        term: { start: "2024-02-13", renews: "2027-02-12", noticeBy: "2026-11-14" },
        usageDetail: "2,380 of 3,150 active in 90 days",
        coverage: { source: "Salesforce login history", asOf: "2026-10-26", matchedPct: 97 },
    },
    "adobe-cc": {
        term: { start: "2023-12-03", renews: "2026-12-02", noticeBy: "2026-11-02" },
        usageDetail: "2,650 of 4,200 active in 90 days",
        coverage: {
            source: "Okta sign-ins to Adobe",
            asOf: "2026-10-27",
            matchedPct: 91,
            note: "Adobe exposes no per-user activity; the identity provider is the source",
        },
    },
    "zoom-enterprise": {
        term: { start: "2026-03-01", renews: "2027-02-28", noticeBy: "2027-01-29" },
        usageDetail: "94% of seats assigned after 410 joiners",
        coverage: { source: "Zoom admin usage report", asOf: "2026-10-28", matchedPct: 99 },
    },
    "snowflake-capacity": {
        term: { start: "2026-04-15", renews: "2027-04-14", noticeBy: "2027-02-14" },
        // $3.65M consumed at 54% of the term; at the pace, $0.9M over the
        // commitment by its end, billed at the on-demand rate: $1.1M.
        commitment: { consumedToDate: 3_650_000, onDemandPremiumPct: 20 },
        usageDetail: "$7.1M a year against a $6.0M commitment",
        coverage: { source: "Snowflake ACCOUNT_USAGE metering", asOf: "2026-10-29" },
    },
    "aws-edp": {
        usageDetail: "$14.8M a year against a $14.0M commitment",
        coverage: { source: "AWS Cost and Usage Report", asOf: "2026-10-28" },
    },
    "databricks-commit": {
        term: { start: "2024-06-16", renews: "2027-06-15", noticeBy: "2027-03-15" },
        usageDetail: "$4.0M a year against a $3.2M commitment",
        coverage: { source: "Databricks system billing tables", asOf: "2026-10-29" },
    },
    "azure-openai": {
        usageDetail: "$2.1M a year against a $1.5M commitment",
        coverage: { source: "Azure cost export", asOf: "2026-10-28" },
    },
    "splunk-ingest": {
        term: { start: "2024-07-01", renews: "2027-06-30", noticeBy: "2027-03-31" },
        usageDetail: "1.7 of 2.4 TB a day ingested",
        coverage: { source: "Splunk license usage report", asOf: "2026-10-27" },
    },
    "dynatrace-hosts": {
        usageDetail: "1,460 of 1,800 host units in use",
        coverage: { source: "Dynatrace account usage", asOf: "2026-10-27" },
    },
};

/** Licenses bought and users active in 90 days, for the seat deals with usage connected. */
const SEATS: Record<string, { bought: number; active: number }> = {
    "microsoft-ea": { bought: 12_400, active: 8_900 },
    "salesforce-sales-cloud": { bought: 3_150, active: 2_380 },
    "adobe-cc": { bought: 4_200, active: 2_650 },
    "zoom-enterprise": { bought: 2_600, active: 2_450 },
    "servicenow-itsm": { bought: 5_200, active: 4_100 },
    "okta-workforce": { bought: 9_000, active: 8_100 },
    "atlassian-cloud": { bought: 2_500, active: 2_100 },
    "bloomberg-terminal": { bought: 380, active: 345 },
    "chatgpt-enterprise": { bought: 2_500, active: 1_700 },
};

for (const deal of DEALS) {
    const extra = DEAL_EXTRAS[deal.id];
    if (extra) Object.assign(deal, extra);
    // Every commitment bills consumption past it at a 20% premium unless said otherwise.
    if (deal.type === "consumption" && !deal.commitment) deal.commitment = { onDemandPremiumPct: 20 };
    const seats = SEATS[deal.id];
    if (seats) {
        deal.seats = seats;
        // The utilization is the seats' ratio, so the two never disagree.
        deal.utilizationPct = Math.round((seats.active / seats.bought) * 100);
    }
}

/** License utilization benchmarks, percent: Zylo's SaaS Management Index (2025 average; top quartile). */
export const UTILIZATION_BENCHMARK = { market: 54, topQuartile: 80 };

/** Spend with usage connected against a year ago, in percent. */
export const SPEND_VS_LAST_YEAR = { connected: 21 };

// ---- People --------------------------------------------------------------

/** The negotiation owners and assignees, by the initials the deals carry. */
export const PEOPLE: Record<string, string> = {
    RS: "Rachel Sato",
    AM: "Adrian Meyer",
    LG: "Lena Graf",
    JM: "Jonas Mills",
    PK: "Priya Kapoor",
    TH: "Tom Hale",
    // Function owners chased for seat reviews.
    DW: "Dana Whitfield",
    MO: "Marcus Oyelaran",
};

export const personName = (initials: string): string => PEOPLE[initials] ?? initials;

// ---- Projects ------------------------------------------------------------

/** The two function-owner reviews on Microsoft read as one chase on the Pipeline. */
export const OWNER_REVIEW_GROUP = "owner_review";

const item = (
    projectId: string,
    id: string,
    stage: ActionItem["stage"],
    title: string,
    origin: ActionItem["origin"],
    why: string,
    ownerKind: ActionItem["ownerKind"],
    ownerLabel: string,
    due: string,
    status: ActionItem["status"] = "open",
    extra: Pick<ActionItem, "note" | "group"> = {}
): ActionItem => ({
    id: `${projectId}:${id}`,
    projectId,
    stage,
    title,
    origin,
    why,
    ownerKind,
    ownerLabel,
    due,
    status,
    // Deciding on a snapshot requests the usage analysis; reviewing the recommendation requests the playbook.
    ...(id === "decide" ? { action: "request_analysis" as const } : {}),
    ...(id === "request-playbook" ? { action: "request_playbook" as const } : {}),
    ...extra,
});

/** The item every proposed project opens with: the user's call on the snapshot. */
const DECIDE = "Decide: renegotiate or let it renew";

/** A project closed in the given month, every stage done, with what it realized against its target. */
const closedProject = (
    id: string,
    name: string,
    dealId: string,
    origin: UsageProject["origin"],
    trigger: string,
    owner: string,
    estSavings: number,
    realizedSavings: number,
    month: string
): UsageProject[] => [
    {
        id,
        name,
        dealId,
        origin,
        trigger,
        stage: "closed",
        estSavings,
        realizedSavings,
        analyses: [
            { kind: "snapshot", revision: 1, publishedAt: `${month}-02`, usageTo: `${month}-01` },
            { kind: "usage", revision: 1, publishedAt: `${month}-09`, usageTo: `${month}-07` },
            { kind: "playbook", revision: 1, publishedAt: `${month}-16`, round: 1 },
        ],
        actionItems: [
            item(id, "decide", "snapshot", DECIDE, origin, trigger, "user", owner, `${month}-04`, "done"),
            item(id, "request-playbook", "usage", "Review the recommendation and request the playbook", origin, trigger, "user", owner, `${month}-11`, "done"),
            item(id, "sign", "playbook", "Sign the renewal", origin, "Agreed terms", "user", owner, `${month}-26`, "done"),
        ],
    },
];

export const PROJECTS: UsageProject[] = [
    {
        id: "usage-adobe",
        name: "Creative Cloud renewal",
        dealId: "adobe-cc",
        origin: "identified",
        trigger: "4,200 bought, 2,650 active; notice due in 3 days",
        stage: "proposed",
        estSavings: 800_000,
        analyses: [{ kind: "snapshot", revision: 1, publishedAt: "2026-10-29", usageTo: "2026-10-27" }],
        actionItems: [
            item("usage-adobe", "decide", "snapshot", DECIDE, "identified", "4,200 bought, 2,650 active; notice due 2 Nov", "user", "RS", "2026-10-28"),
        ],
        isNew: true,
    },
    {
        id: "usage-microsoft",
        name: "EA renewal",
        dealId: "microsoft-ea",
        origin: "renewal",
        trigger: "72% utilization; notice in 62 days",
        stage: "playbook",
        // The snapshot's rough range; the usage analysis's quantity and edition
        // lever ($1.9M); the playbook's target adds the price lever ($1.0M).
        snapshotEstimate: [2_400_000, 3_400_000],
        usageSavings: 1_900_000,
        estSavings: 2_900_000,
        analyses: [
            { kind: "snapshot", revision: 1, publishedAt: "2026-09-28", usageTo: "2026-09-25" },
            { kind: "usage", revision: 1, publishedAt: "2026-10-03", usageTo: "2026-09-26" },
            { kind: "usage", revision: 2, publishedAt: "2026-10-28", usageTo: "2026-10-24" },
            { kind: "playbook", revision: 1, publishedAt: "2026-10-29", round: 1, inProgress: true },
        ],
        actionItems: [
            item("usage-microsoft", "decide", "snapshot", DECIDE, "renewal", "Renewal approaching; notice by 31 Dec 2026", "user", "RS", "2026-10-05", "done"),
            item("usage-microsoft", "kickoff", "usage", "Kickoff and data request", "renewal", "Data for the usage analysis: M365 export, Entra sign-ins, Workday", "user", "RS", "2026-10-02", "done"),
            item("usage-microsoft", "owner-reviews", "usage", "Send owner reviews (five functions)", "identified", "2,390 inactive seats to confirm with the function owners", "user", "RS", "2026-10-10", "done"),
            item("usage-microsoft", "request-playbook", "usage", "Review the recommendation and request the playbook", "renewal", "10,400 licenses recommended; round 1 before the notice date", "user", "RS", "2026-10-29", "done"),
            item("usage-microsoft", "review-cs", "playbook", "Chase Client services review (510 seats)", "identified", "510 seats inactive; the owner confirmed 470 to release", "function_owner", "DW", "2026-11-21", "done", { group: OWNER_REVIEW_GROUP }),
            item("usage-microsoft", "review-ops", "playbook", "Chase Operations review (640 seats)", "identified", "640 seats inactive, awaiting the owner's answer", "function_owner", "MO", "2026-11-21", "open", { group: OWNER_REVIEW_GROUP, note: "In progress" }),
            item("usage-microsoft", "mca-e", "playbook", "MCA-E price comparison", "requested", "You asked whether MCA-E beats the EA at 10,400 licenses", "infinityloop", "InfinityLoop", "2026-11-21", "open", { note: "In progress" }),
            item("usage-microsoft", "copilot", "playbook", "Copilot usage case", "identified", "460 of 1,000 Copilot seats inactive; case written up", "teammate", "AM", "2026-11-28", "done"),
            item("usage-microsoft", "cio-review", "playbook", "Playbook review with CIO office", "renewal", "Economic buyer sign-off before round 2", "user", "RS", "2026-12-05", "open", { note: "In progress" }),
            item("usage-microsoft", "legal", "playbook", "Legal review of reduction clause", "identified", "Contract review found no right to reduce", "teammate", "LG", "2026-12-12"),
            item("usage-microsoft", "notice", "playbook", "Notice decision", "renewal", "Notice by 31 Dec 2026", "user", "RS", "2026-12-31"),
        ],
    },
    {
        id: "usage-salesforce",
        name: "Sales Cloud renewal",
        dealId: "salesforce-sales-cloud",
        origin: "renewal",
        trigger: "3,150 bought, 2,380 active; price above peer median",
        stage: "playbook",
        estSavings: 1_200_000,
        analyses: [
            { kind: "snapshot", revision: 1, publishedAt: "2026-10-05", usageTo: "2026-10-02" },
            { kind: "usage", revision: 1, publishedAt: "2026-10-12", usageTo: "2026-10-09" },
            { kind: "playbook", revision: 1, publishedAt: "2026-10-26", round: 1 },
        ],
        actionItems: [
            item("usage-salesforce", "decide", "snapshot", DECIDE, "renewal", "Renewal approaching; notice by 14 Nov 2026", "user", "JM", "2026-10-07", "done"),
            item("usage-salesforce", "request-playbook", "usage", "Review the recommendation and request the playbook", "renewal", "2,380 of 3,150 active; price above the peer median", "user", "JM", "2026-10-20", "done"),
            item("usage-salesforce", "start", "playbook", "Start negotiation", "renewal", "Playbook round 1 delivered; notice by 14 Nov", "user", "JM", "2026-11-14"),
        ],
    },
    {
        id: "usage-snowflake",
        name: "Capacity commitment review",
        dealId: "snowflake-capacity",
        origin: "identified",
        trigger: "118% of commitment pace; $1.1M overage ahead",
        stage: "proposed",
        estSavings: 1_100_000,
        analyses: [{ kind: "snapshot", revision: 1, publishedAt: "2026-10-29", usageTo: "2026-10-28" }],
        actionItems: [
            item("usage-snowflake", "decide", "snapshot", DECIDE, "identified", "118% of commitment pace; $1.1M overage ahead", "user", "RS", "2026-10-30"),
        ],
        isNew: true,
    },
    {
        id: "usage-zoom",
        name: "Seat check after the joiners",
        dealId: "zoom-enterprise",
        origin: "requested",
        trigger: "94% of seats assigned after 410 joiners",
        stage: "usage_analysis",
        // No target until the analysis is published.
        estSavings: 0,
        analyses: [],
        actionItems: [
            item("usage-zoom", "analysis", "usage", "Usage analysis", "requested", "You requested a usage analysis after 410 joiners", "infinityloop", "InfinityLoop", "2026-10-31", "open", { note: "In progress" }),
        ],
    },
    {
        id: "usage-databricks",
        name: "Commitment review",
        dealId: "databricks-commit",
        origin: "identified",
        trigger: "125% of pace",
        stage: "negotiating",
        estSavings: 800_000,
        realizedSavings: 600_000,
        analyses: [
            { kind: "snapshot", revision: 1, publishedAt: "2026-09-10", usageTo: "2026-09-08" },
            { kind: "usage", revision: 1, publishedAt: "2026-09-18", usageTo: "2026-09-15" },
            { kind: "playbook", revision: 1, publishedAt: "2026-09-25", round: 1 },
            { kind: "playbook", revision: 2, publishedAt: "2026-10-20", round: 2 },
        ],
        actionItems: [
            item("usage-databricks", "decide", "snapshot", DECIDE, "identified", "125% of pace; notice by 15 Mar 2027", "user", "PK", "2026-09-12", "done"),
            item("usage-databricks", "kickoff", "usage", "Kickoff and data request", "identified", "Workspace consumption export for the usage analysis", "user", "PK", "2026-09-15", "done"),
            item("usage-databricks", "request-playbook", "usage", "Review the recommendation and request the playbook", "identified", "Rebalance the commitment before the overage lands", "user", "PK", "2026-09-22", "done"),
            item("usage-databricks", "round-1", "playbook", "Round 1: present the rebalanced commitment", "identified", "Playbook round 1 delivered", "user", "PK", "2026-10-02", "done"),
            item("usage-databricks", "round-2", "playbook", "Round 2: counter on the unit rate", "identified", "Databricks offered $0.6M off; $0.2M still on the table", "user", "PK", "2026-10-24", "done"),
            item("usage-databricks", "sign", "playbook", "Review the final offer and sign the amendment", "identified", "Final offer due from Databricks; amendment to sign by 20 Nov", "user", "PK", "2026-11-20", "open", { note: "In progress" }),
        ],
    },
    {
        id: "usage-splunk",
        name: "Ingest commitment review",
        dealId: "splunk-ingest",
        origin: "identified",
        trigger: "71% of 2.4 TB a day used",
        stage: "negotiating",
        estSavings: 1_300_000,
        realizedSavings: 1_250_000,
        analyses: [
            { kind: "snapshot", revision: 1, publishedAt: "2026-08-20", usageTo: "2026-08-18" },
            { kind: "usage", revision: 1, publishedAt: "2026-08-28", usageTo: "2026-08-25" },
            { kind: "playbook", revision: 1, publishedAt: "2026-09-05", round: 1 },
            { kind: "playbook", revision: 2, publishedAt: "2026-10-10", round: 2 },
        ],
        actionItems: [
            item("usage-splunk", "decide", "snapshot", DECIDE, "identified", "71% of 2.4 TB a day used; renewal in Jun 2027", "user", "LG", "2026-08-22", "done"),
            item("usage-splunk", "kickoff", "usage", "Kickoff and data request", "identified", "Ingest volumes by index for the usage analysis", "user", "LG", "2026-08-24", "done"),
            item("usage-splunk", "request-playbook", "usage", "Review the recommendation and request the playbook", "identified", "Right-size to 1.8 TB a day at the renewal", "user", "LG", "2026-09-01", "done"),
            item("usage-splunk", "round-1", "playbook", "Round 1: present the 1.8 TB tier", "identified", "Playbook round 1 delivered", "user", "LG", "2026-09-12", "done"),
            item("usage-splunk", "round-2", "playbook", "Round 2: counter on the ingest tier", "identified", "Splunk agreed to 1.8 TB a day at $1.25M a year off", "user", "LG", "2026-10-14", "done"),
            item("usage-splunk", "sign", "playbook", "Sign the renewal at 1.8 TB a day", "identified", "Agreed terms; signature by 10 Nov", "user", "LG", "2026-11-10"),
        ],
    },
    // ---- Closed this year: the realized savings the Pipeline reports ----------
    ...closedProject("usage-servicenow", "ITSM Pro renewal", "servicenow-itsm", "renewal", "79% utilization at the renewal", "JM", 1_000_000, 900_000, "2026-03"),
    ...closedProject("usage-okta", "Workforce Identity renewal", "okta-workforce", "identified", "90% utilization; price above the peer median", "LG", 500_000, 450_000, "2026-06"),
    ...closedProject("usage-atlassian", "Cloud Enterprise renewal", "atlassian-cloud", "renewal", "84% utilization at the renewal", "LG", 350_000, 300_000, "2026-09"),
];

// ---- Microsoft EA detail -------------------------------------------------

export const E5_UNIT_PRICE = 684; // $57 a month
export const E3_UNIT_PRICE = 432; // $36 a month

export const MICROSOFT_EA: SeatDealDetail = {
    dealId: "microsoft-ea",
    driftLine:
        "Report v2 of 28 Oct 2026 (usage to 24 Oct). Since then: 14 more seats inactive, no change to recommendation.",
    paidFor: { licenses: 12_400, assigned: 11_850, neverAssigned: 550, unitPrice: E5_UNIT_PRICE },
    used: { active90: 8_900, noSignIn90: 2_390, departed: 560, fitE3: 2_100 },
    needed: { total: 10_400, e5: 8_300, e3: 2_100, e5Range: [7_150, 8_600], e3UnitPrice: E3_UNIT_PRICE },
    waterfall: [
        { label: "Licenses bought today", value: 12_400, kind: "total" },
        { label: "Never assigned to a person", value: -550, kind: "minus" },
        { label: "Held by staff who have left", value: -560, kind: "minus" },
        { label: "No sign-in in 90 days, confirmed not needed by the function owner", value: -1_240, kind: "minus" },
        { label: "Planned hires in licensed functions to March 2027", value: 350, kind: "plus" },
        { label: "Licenses needed next term", value: 10_400, kind: "total" },
    ],
    skus: [
        { sku: "Microsoft 365 E5", bought: 12_400, assigned: 11_850, active90: 8_900, inactive: 2_390, departed: 560, fitLower: "2,100 to E3", unitPrice: 684, costAtRisk: 2_390_000, recommendation: "Renew 8,300 E5 and 2,100 E3" },
        { sku: "Microsoft 365 Copilot", bought: 1_000, assigned: 940, active90: 540, inactive: 400, departed: 30, fitLower: "none", unitPrice: 360, costAtRisk: 180_000, recommendation: "Re-size to 600" },
        { sku: "Visio Plan 2", bought: 600, assigned: 580, active90: 310, inactive: 270, departed: 20, fitLower: "120 to Plan 1", unitPrice: 180, costAtRisk: 60_000, recommendation: "Move 120 users to Plan 1" },
        { sku: "Project Plan 3", bought: 400, assigned: 390, active90: 180, inactive: 210, departed: 10, fitLower: "none", unitPrice: 360, costAtRisk: 80_000, recommendation: "Release 210 at renewal" },
        { sku: "Power BI Premium per user", bought: 800, assigned: 760, active90: 610, inactive: 150, departed: 15, fitLower: "none", unitPrice: 240, costAtRisk: 50_000, recommendation: "Release inactive at renewal" },
    ],
    cohorts: [
        { function: "Client services", activeUsers: 2_360, fitE3: 900, keepE5: 1_460, awaitingReview: 510, review: { kind: "due", on: "2026-11-21" } },
        { function: "Advisory and wealth", activeUsers: 2_050, fitE3: 300, keepE5: 1_750, awaitingReview: 0, review: { kind: "confirmed", on: "2026-10-24" } },
        { function: "Technology", activeUsers: 2_300, fitE3: 100, keepE5: 2_200, awaitingReview: 0, review: { kind: "confirmed", on: "2026-10-24" } },
        { function: "Operations", activeUsers: 1_500, fitE3: 600, keepE5: 900, awaitingReview: 640, review: { kind: "due", on: "2026-11-21" } },
        { function: "Corporate functions", activeUsers: 690, fitE3: 200, keepE5: 490, awaitingReview: 0, review: { kind: "confirmed", on: "2026-10-24" } },
    ],
    people: [
        { id: "u-48a2f1", function: "Operations", employment: "Active", lastSignIn: "2026-06-14", daysInactive: 132, daysUsed: { d30: 0, d60: 0, d90: 0 }, e5Features: 0, recommendation: "Release", state: "awaiting_owner" },
        { id: "u-9c11be", function: "Operations", employment: "Active", lastSignIn: "2026-07-02", daysInactive: 114, daysUsed: { d30: 0, d60: 0, d90: 0 }, e5Features: 0, recommendation: "Release", state: "awaiting_owner" },
        { id: "u-b23a90", function: "Operations", employment: "On leave since 1 Aug", lastSignIn: "2026-07-29", daysInactive: 87, daysUsed: { d30: 0, d60: 0, d90: 0 }, e5Features: 0, recommendation: "Review", state: "awaiting_owner" },
        { id: "u-e61c07", function: "Operations", employment: "Active", lastSignIn: "2026-08-08", daysInactive: 77, daysUsed: { d30: 0, d60: 0, d90: 0 }, e5Features: 0, recommendation: "Downgrade to E3", state: "awaiting_owner" },
        { id: "u-7d20aa", function: "Operations", employment: "Active", lastSignIn: "2026-09-11", daysInactive: 49, daysUsed: { d30: 0, d60: 3, d90: 3 }, e5Features: 0, recommendation: "Downgrade to E3", state: "awaiting_owner" },
        { id: "u-3f8d12", function: "Operations", employment: "Active", lastSignIn: "2026-10-23", daysInactive: 1, e5Features: 1, recommendation: "Keep", state: "confirmed" },
        { id: "u-c4a5d8", function: "Operations", employment: "Left 14 Sep 2026", lastSignIn: "2026-09-12", daysInactive: 42, e5Features: 2, recommendation: "Release now", state: "ready" },
        { id: "u-5be2c3", function: "Operations", employment: "Active, shared device", lastSignIn: "2026-10-24", daysInactive: 0, e5Features: 1, recommendation: "Keep", state: "exempt" },
    ],
    escalation:
        "Reminder sent 27 Oct to the two outstanding reviews. Escalates to the business owner on 14 Nov if unanswered, then to the CIO office after 7 more days.",
    rules: [
        { label: "Activity threshold", value: "90 days" },
        { label: "Grace after release notice", value: "14 days" },
        { label: "Departed staff", value: "Release 30 days after leaving" },
        { label: "Edition fit window", value: "90 days, 5 E5-only features" },
        { label: "Exemptions", value: "85, reviewed quarterly" },
        { label: "Owner escalation", value: "14 days, then business owner" },
    ],
    renewalEffect: { function: "Operations", seats: 640, allRelease: 7_660, halfRelease: 7_980, noneRelease: 8_300, per100: 68_000 },
    // Active users against licenses bought, by month; the next term from
    // April 2027 at the recommended quantity.
    trend: [
        { month: "2025-11", bought: 12_400, active: 9_620 },
        { month: "2025-12", bought: 12_400, active: 9_580 },
        { month: "2026-01", bought: 12_400, active: 9_540 },
        { month: "2026-02", bought: 12_400, active: 9_490 },
        { month: "2026-03", bought: 12_400, active: 9_420 },
        { month: "2026-04", bought: 12_400, active: 9_380 },
        { month: "2026-05", bought: 12_400, active: 9_310 },
        { month: "2026-06", bought: 12_400, active: 9_240 },
        { month: "2026-07", bought: 12_400, active: 9_150 },
        { month: "2026-08", bought: 12_400, active: 9_080 },
        { month: "2026-09", bought: 12_400, active: 8_990 },
        { month: "2026-10", bought: 12_400, active: 8_900 },
        { month: "2026-11", bought: 12_400, active: 8_880, projected: true },
        { month: "2026-12", bought: 12_400, active: 8_850, projected: true },
        { month: "2027-01", bought: 12_400, active: 8_830, projected: true },
        { month: "2027-02", bought: 12_400, active: 8_810, projected: true },
        { month: "2027-03", bought: 12_400, active: 8_800, projected: true },
        { month: "2027-04", bought: 10_400, active: 9_150, projected: true },
        { month: "2027-05", bought: 10_400, active: 9_200, projected: true },
        { month: "2027-06", bought: 10_400, active: 9_250, projected: true },
    ],
    analysisNotes: [
        "1,150 seats under owner review (Client services 510, Operations 640), due 21 Nov.",
        "2,100 active users used none of the five E5-only features in 90 days and fit E3.",
        "550 licenses never assigned and 560 held by departed staff can be released now.",
    ],
};

/** The seat-deal details the agreement page can render, by deal. */
export const SEAT_DETAILS: Record<string, SeatDealDetail> = {
    [MICROSOFT_EA.dealId]: MICROSOFT_EA,
};

/** The only deal, vendor and project the demo drills into. */
export const DRILLABLE_DEAL_ID = "microsoft-ea";

/** The agreements with a page of their own: one seat deal, one consumption deal. */
export const DRILLABLE_DEAL_IDS = ["microsoft-ea", "snowflake-capacity"];

// ---- Snowflake capacity: the consumption page's detail ----------------------

export const CONSUMPTION_DETAILS: Record<string, ConsumptionDealDetail> = {
    "snowflake-capacity": {
        dealId: "snowflake-capacity",
        // Cumulative by month end; October to the 29th. Ends at the consumed-to-date figure.
        monthly: [
            { month: "2026-04", consumed: 250_000 },
            { month: "2026-05", consumed: 720_000 },
            { month: "2026-06", consumed: 1_220_000 },
            { month: "2026-07", consumed: 1_760_000 },
            { month: "2026-08", consumed: 2_360_000 },
            { month: "2026-09", consumed: 2_980_000 },
            { month: "2026-10", consumed: 3_650_000 },
        ],
        workloads: [
            { workload: "Data engineering pipelines", sharePct: 36, annualized: 2_560_000, sinceJulyPct: 8 },
            { workload: "Analytics and reporting", sharePct: 25, annualized: 1_780_000, sinceJulyPct: 5 },
            { workload: "Data science", sharePct: 18, annualized: 1_280_000, sinceJulyPct: 14 },
            { workload: "Development and test", sharePct: 16, annualized: 1_140_000, sinceJulyPct: 60 },
            { workload: "Other", sharePct: 5, annualized: 340_000, sinceJulyPct: 2 },
        ],
        insight:
            "Development and test warehouses run without auto-suspend on 11 of 14 environments. That alone explains $0.35M of the annual pace.",
        terms: {
            capacityRate: "$2.40 a credit",
            onDemandRate: "$2.88 a credit, 20% higher",
            rollover: "None",
            earlyRenewal: "Allowed; unused balance carries into the new term",
        },
    },
};
/** The projects with a page of their own: Microsoft's (usage analysis and playbook) and Snowflake's (snapshot). */
export const DRILLABLE_PROJECT_IDS = ["usage-microsoft", "usage-snowflake"];
export const isDrillableProject = (projectId: string) => DRILLABLE_PROJECT_IDS.includes(projectId);
